import React, { useEffect, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { Plus, Trash2 } from "lucide-react";
import { db } from "../../firebase";
import {
	BOOKING_SERVICES_SETTINGS_ID,
	defaultBookingServices,
	normaliseBookingServices,
	parseZarCents,
	packagePriceLabel,
} from "../../data/bookingServices";

function copy(value) {
	return JSON.parse(JSON.stringify(value));
}

function newId(prefix) {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return `${prefix}-${crypto.randomUUID()}`;
	}
	return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createService() {
	return {
		id: newId("service"),
		title: "",
		tagline: "",
		duration: 60,
		active: true,
		allowOnlinePayment: false,
		modes: ["in-person", "virtual"],
		packages: [{ id: newId("option"), label: "Session", priceType: "fixed", priceCents: null, priceInput: "" }],
	};
}

function toEditable(service) {
	const next = copy(service);
	next.packages = next.packages.map((pkg) => ({
		...pkg,
		priceInput: pkg.priceType === "fixed" && Number.isFinite(Number(pkg.priceCents))
			? String(Number(pkg.priceCents) / 100)
			: "",
	}));
	return next;
}

function validateService(service, services) {
	const title = String(service.title || "").trim();
	if (!title) throw new Error("A service name is required.");
	if (!Number.isFinite(Number(service.duration)) || Number(service.duration) < 15 || Number(service.duration) > 480) {
		throw new Error("Session duration must be between 15 and 480 minutes.");
	}
	if (!Array.isArray(service.modes) || !service.modes.length) throw new Error("Choose at least one meeting format.");
	if (!Array.isArray(service.packages) || !service.packages.length) throw new Error("Add at least one price or enquiry option.");
	if (services.some((item) => item.id === service.id && item.title.trim().toLowerCase() === title.toLowerCase())) {
		throw new Error("Another service already has that name.");
	}

	const packageIds = new Set();
	const packages = service.packages.map((pkg, index) => {
		const label = String(pkg.label || "").trim();
		if (!label) throw new Error(`Option ${index + 1} needs a name.`);
		const id = String(pkg.id || `option-${Date.now()}-${index}`);
		if (packageIds.has(id)) throw new Error("Each service option needs its own identifier.");
		packageIds.add(id);
		if (pkg.priceType === "on-request") return { id, label, priceType: "on-request", priceCents: null };
		const priceCents = parseZarCents(pkg.priceInput ?? pkg.priceCents);
		if (!Number.isInteger(priceCents) || priceCents <= 0) throw new Error(`${label} needs a price greater than R 0.00.`);
		return { id, label, priceType: "fixed", priceCents };
	});

	return {
		...service,
		title,
		tagline: service.tagline.trim(),
		duration: Math.round(Number(service.duration)),
		active: service.active !== false,
		allowOnlinePayment: service.allowOnlinePayment === true,
		modes: service.modes.filter((mode) => mode === "in-person" || mode === "virtual"),
		packages,
	};
}

const ServiceManagementPanel = ({ standalone = false }) => {
	const [services, setServices] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);
	const [editing, setEditing] = useState(null);
	const [isNew, setIsNew] = useState(false);

	useEffect(() => {
		let active = true;
		Promise.all([
			getDoc(doc(db, "settings", BOOKING_SERVICES_SETTINGS_ID)),
			getDoc(doc(db, "settings", "servicePricing")),
		]).then(([catalogueSnapshot, legacyPricingSnapshot]) => {
			if (!active) return;
			const legacyPrices = legacyPricingSnapshot.exists() ? legacyPricingSnapshot.data() || {} : {};
			setServices(catalogueSnapshot.exists()
				? normaliseBookingServices(catalogueSnapshot.data()?.services)
				: defaultBookingServices(legacyPrices));
		}).catch((err) => {
			if (active) setError(err.message || "Could not load the service catalogue.");
		}).finally(() => {
			if (active) setLoading(false);
		});
		return () => { active = false; };
	}, []);

	const persist = async (nextServices, message) => {
		setSaving(true);
		setError(null);
		try {
			await setDoc(doc(db, "settings", BOOKING_SERVICES_SETTINGS_ID), {
				services: nextServices,
				updatedAt: serverTimestamp(),
			}, { merge: true });
			setServices(nextServices);
			setEditing(null);
			setIsNew(false);
			setNotice(message);
			setTimeout(() => setNotice(null), 3000);
		} catch (err) {
			setError(err.message || "Could not save services.");
		} finally {
			setSaving(false);
		}
	};

	const saveEditing = async () => {
		try {
			const otherServices = services.filter((service) => service.id !== editing.id);
			const savedService = validateService(editing, otherServices);
			const nextServices = isNew
				? [...services, savedService]
				: services.map((service) => service.id === savedService.id ? savedService : service);
			await persist(nextServices, `${savedService.title} saved.`);
		} catch (err) {
			setError(err.message);
		}
	};

	const deleteService = async (service) => {
		if (!window.confirm(`Delete ${service.title}? Existing bookings will be kept, but clients will no longer be able to select it. If it is the last service, the booking page will show that bookings are unavailable.`)) return;
		await persist(services.filter((item) => item.id !== service.id), `${service.title} deleted.`);
	};

	const updateEditing = (field, value) => setEditing((current) => ({ ...current, [field]: value }));
	const updatePackage = (index, field, value) => setEditing((current) => ({
		...current,
		packages: current.packages.map((pkg, packageIndex) => packageIndex === index ? { ...pkg, [field]: value } : pkg),
	}));
	const updateModes = (mode, checked) => setEditing((current) => ({
		...current,
		modes: checked ? [...new Set([...current.modes, mode])] : current.modes.filter((item) => item !== mode),
	}));

	if (loading) return <div className="adm-bookings-empty">Loading services…</div>;

	return (
		<div style={standalone ? undefined : { marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--adm-line)" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
				<div>
					<div style={{ fontWeight: 650, fontSize: 13.5 }}>Booking services</div>
					<div style={{ marginTop: 3, fontSize: 12.5, color: "var(--adm-ink-soft)", lineHeight: 1.45 }}>
						Add, change, hide, or remove services and their pricing options. Changes appear on the booking page immediately.
					</div>
				</div>
				<button type="button" className="adm-primary-btn" onClick={() => { setEditing(createService()); setIsNew(true); setError(null); }} disabled={Boolean(editing) || saving} style={{ whiteSpace: "nowrap" }}>
					<Plus size={15} aria-hidden="true" /> Add service
				</button>
			</div>

			{error && <div className="adm-query-error" style={{ marginTop: 10 }}>{error}</div>}
			{notice && <div className="adm-user-form__success" role="status" style={{ marginTop: 10 }}>{notice}</div>}

			<div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
				{services.map((service) => (
					<div key={service.id} style={{ border: "1px solid var(--adm-line)", borderRadius: 9, padding: "10px 11px", background: "var(--adm-surface-raised)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
							<div>
								<div style={{ fontWeight: 600, fontSize: 13 }}>{service.title} {!service.active && <span style={{ color: "var(--adm-ink-faint)", fontWeight: 500 }}>(hidden)</span>}</div>
								<div style={{ marginTop: 2, fontSize: 12, color: "var(--adm-ink-soft)" }}>{service.packages.map((pkg) => `${pkg.label}: ${packagePriceLabel(pkg)}`).join(" · ")}</div>
							</div>
							<div style={{ display: "flex", gap: 8 }}>
								<button type="button" className="adm-text-btn" onClick={() => { setEditing(toEditable(service)); setIsNew(false); setError(null); }} disabled={Boolean(editing) || saving}>Edit</button>
								<button type="button" className="adm-text-btn adm-text-btn--danger" onClick={() => deleteService(service)} disabled={Boolean(editing) || saving}><Trash2 size={14} aria-hidden="true" /> Delete</button>
							</div>
						</div>
					</div>
				))}
			</div>

			{editing && (
				<div style={{ marginTop: 12, padding: 12, border: "1px solid var(--adm-accent, #8a6a44)", borderRadius: 10, background: "var(--adm-surface)" }}>
					<div style={{ fontWeight: 650, marginBottom: 10 }}>{isNew ? "Add booking service" : `Edit ${editing.title}`}</div>
					<div className="adm-field-row"><label className="adm-field-label">Service name</label><input className="adm-field-input" value={editing.title} onChange={(event) => updateEditing("title", event.target.value)} placeholder="e.g. Family Counselling" /></div>
					<div className="adm-field-row" style={{ marginTop: 9 }}><label className="adm-field-label">Description (optional)</label><input className="adm-field-input" value={editing.tagline} onChange={(event) => updateEditing("tagline", event.target.value)} placeholder="A short description shown to clients" /></div>
					<div className="adm-field-row" style={{ marginTop: 9 }}><label className="adm-field-label">Appointment length</label><input className="adm-field-input" type="number" min="15" max="480" value={editing.duration} onChange={(event) => updateEditing("duration", event.target.value)} /></div>

					<div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 12, fontSize: 12.5 }}>
						<label><input type="checkbox" checked={editing.active !== false} onChange={(event) => updateEditing("active", event.target.checked)} /> Show on booking page</label>
						<label><input type="checkbox" checked={editing.allowOnlinePayment === true} onChange={(event) => updateEditing("allowOnlinePayment", event.target.checked)} /> Allow online payment for fixed-price options</label>
						<label><input type="checkbox" checked={editing.modes.includes("in-person")} onChange={(event) => updateModes("in-person", event.target.checked)} /> In person</label>
						<label><input type="checkbox" checked={editing.modes.includes("virtual")} onChange={(event) => updateModes("virtual", event.target.checked)} /> Virtual</label>
					</div>

					<div style={{ marginTop: 14, fontWeight: 600, fontSize: 12.5 }}>Pricing / enquiry options</div>
					<div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
						{editing.packages.map((pkg, index) => (
							<div key={pkg.id} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 130px minmax(0, 1fr) auto", gap: 7, alignItems: "center" }}>
								<input className="adm-field-input" value={pkg.label} onChange={(event) => updatePackage(index, "label", event.target.value)} placeholder="Option name" aria-label={`Option ${index + 1} name`} />
								<select className="adm-field-input" value={pkg.priceType} onChange={(event) => updatePackage(index, "priceType", event.target.value)} aria-label={`Option ${index + 1} price type`}><option value="fixed">Set price</option><option value="on-request">Price on request</option></select>
								{pkg.priceType === "fixed" ? <input className="adm-field-input" value={pkg.priceInput ?? ""} onChange={(event) => updatePackage(index, "priceInput", event.target.value)} placeholder="e.g. 1475.00" aria-label={`Option ${index + 1} price`} /> : <span style={{ color: "var(--adm-ink-soft)", fontSize: 12 }}>Client sends an enquiry</span>}
								<button type="button" className="adm-text-btn adm-text-btn--danger" onClick={() => updateEditing("packages", editing.packages.filter((_, packageIndex) => packageIndex !== index))} disabled={editing.packages.length === 1}>Remove</button>
							</div>
						))}
					</div>
					<button type="button" className="adm-text-btn" style={{ marginTop: 8 }} onClick={() => updateEditing("packages", [...editing.packages, { id: newId("option"), label: "", priceType: "fixed", priceCents: null, priceInput: "" }])}><Plus size={14} aria-hidden="true" /> Add option</button>

					<div style={{ display: "flex", gap: 9, marginTop: 14 }}>
						<button type="button" className="adm-secondary-btn" onClick={() => { setEditing(null); setIsNew(false); setError(null); }} disabled={saving}>Cancel</button>
						<button type="button" className="adm-primary-btn" onClick={saveEditing} disabled={saving}>{saving ? "Saving…" : "Save service"}</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default ServiceManagementPanel;
